const VOUCHER_CATEGORY_KEYWORDS = ['VOUCHER'];

export const isVoucherCategory = (categoryName = '') => {
  const upper = String(categoryName).toUpperCase();
  return VOUCHER_CATEGORY_KEYWORDS.some((keyword) => upper.includes(keyword));
};

export const voucherSellingPrice = (v) => {
  if (v?.selling_price != null && String(v.selling_price).trim() !== '') {
    return Number(v.selling_price);
  }
  const n = v?.price_non_member ?? v?.price_member;
  return Number(n || 0);
};

export const buildVoucherOptionsFromList = (vouchers, items, getCartQtyForVoucher) => {
  const active = (Array.isArray(vouchers) ? vouchers : []).filter((v) => v && v.status !== false);
  const byCat = {};
  active.forEach((v) => {
    const catKey = (v.voucher_category || 'VOUCHER').toString();
    if (!byCat[catKey]) byCat[catKey] = [];
    byCat[catKey].push(v);
  });
  const keys = Object.keys(byCat).sort((a, b) => a.localeCompare(b));
  const options = [];
  keys.forEach((catKey) => {
    options.push({ value: `header-voucher-${catKey}`, label: catKey, isHeader: true });
    byCat[catKey].forEach((v) => {
      const id = v.id;
      const price = voucherSellingPrice(v);
      const currentCartQty = getCartQtyForVoucher(id);
      const availableQuantity = null;
      const isDisabled = availableQuantity !== null && currentCartQty >= availableQuantity;
      options.push({
        value: `voucher-${id}`,
        label: v.name || 'Voucher',
        right: `$${Number(price).toFixed(2)}`,
        price,
        gst: v.gst,
        name: v.name || 'Voucher',
        category: catKey,
        isVoucher: true,
        availableQuantity,
        fullProductData: v,
        disabled: isDisabled,
      });
    });
  });
  return options;
};

export const createEmptyVoucherCodes = (qty = 1) =>
  Array.from({ length: Math.max(1, qty) }, () => '');

export const normalizeVoucherCodes = (codes = [], qty = 1) => {
  const q = Math.max(1, qty);
  const next = Array.isArray(codes) ? [...codes] : [];
  if (next.length > q) {
    return next.slice(next.length - q);
  }
  while (next.length < q) {
    next.unshift('');
  }
  return next;
};

export const voucherVerifySlotKey = (lineId, codeIdx) => `${lineId}-${codeIdx}`;

export const remapVoucherVerifyForQtyChange = (prev, lineId, beforeCodes, afterCodes) => {
  if (!lineId) return prev;
  const prefix = `${lineId}-`;
  const before = Array.isArray(beforeCodes) ? beforeCodes : [];
  const after = Array.isArray(afterCodes) ? afterCodes : [];
  const byOldIndex = {};
  Object.keys(prev).forEach((k) => {
    if (!k.startsWith(prefix)) return;
    const idx = Number(k.slice(prefix.length));
    if (!Number.isNaN(idx) && prev[k]) byOldIndex[idx] = prev[k];
  });
  const next = { ...prev };
  Object.keys(next).forEach((k) => {
    if (k.startsWith(prefix)) delete next[k];
  });
  after.forEach((serial, newIdx) => {
    const trimmed = String(serial ?? '').trim();
    if (!trimmed) return;
    const oldIdx = before.findIndex((c) => String(c ?? '').trim() === trimmed);
    if (oldIdx >= 0 && byOldIndex[oldIdx]) {
      next[voucherVerifySlotKey(lineId, newIdx)] = byOldIndex[oldIdx];
    }
  });
  return next;
};

export const isVoucherVerifyResponseOk = (resp) =>
  Boolean(
    resp?.success === true ||
      resp?.data?.success === true ||
      (resp?.data && typeof resp.data === 'object' && resp.data.success === true)
  );

export const getVoucherVerifySuccessMessage = (resp) =>
  resp?.data?.message || resp?.message || 'Voucher verified successfully.';

export const getVoucherVerifyErrorMessage = (resp) =>
  resp?.data?.message ||
  resp?.message ||
  (Array.isArray(resp?.errors) ? resp.errors.join(', ') : null) ||
  'Voucher verification failed';

export const extractServiceIdsFromBooking = (booking) => {
  const rawServices =
    (booking && booking.booking_items) ||
    (booking && booking.services) ||
    (booking && booking.booking_services) ||
    [];
  if (!Array.isArray(rawServices)) return [];
  return [
    ...new Set(
      rawServices
        .map((row) => Number(row?.service_id ?? row?.serviceId ?? row?.id))
        .filter((id) => Number.isFinite(id) && id > 0)
    ),
  ];
};

export const isCartVoucherSerialPresent = (cartItems, serial) => {
  const normalized = String(serial || '').trim();
  if (!normalized) return false;
  return cartItems.some((it) => {
    if (!it.isVoucher) return false;
    const codes = normalizeVoucherCodes(it.voucherCodes, it.qty);
    return codes.some((c) => String(c || '').trim() === normalized);
  });
};

export const getVoucherFromVerifyResponse = (resp) =>
  resp?.data?.data?.voucher || resp?.data?.voucher || null;

export const findVoucherCatalogProduct = (catalog, voucherPayload) => {
  const list = Array.isArray(catalog) ? catalog : [];
  if (!voucherPayload) return null;

  const productId =
    voucherPayload.voucher_product_id ??
    voucherPayload.product_id ??
    voucherPayload.voucher_product?.id;
  if (productId != null) {
    const byId = list.find((p) => Number(p.id) === Number(productId));
    if (byId) return byId;
  }

  const targetName = String(voucherPayload.product_name || voucherPayload.name || '')
    .trim()
    .toLowerCase();
  if (!targetName) return null;

  return (
    list.find((p) => String(p.name || '').trim().toLowerCase() === targetName) ||
    list.find((p) => String(p.name || '').trim().toLowerCase().includes(targetName)) ||
    null
  );
};

export const buildOptionFromVoucherCatalogProduct = (product) => {
  const price = voucherSellingPrice(product);
  const catKey = (product.voucher_category || 'VOUCHER').toString();
  return {
    value: `voucher-${product.id}`,
    label: product.name || 'Voucher',
    right: `$${Number(price).toFixed(2)}`,
    price,
    gst: product.gst,
    name: product.name || 'Voucher',
    category: catKey,
    isVoucher: true,
    availableQuantity: null,
    fullProductData: product,
    disabled: false,
  };
};

export const isEditableInputFocused = () => {
  const el = document.activeElement;
  if (!el || el === document.body) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return false;
  if (el.readOnly || el.disabled) return false;
  return true;
};
